/**
 * TanStack Query Client Configuration
 *
 * Basic QueryClient setup with essential configurations.
 * Will be expanded as app requirements become clearer.
 */

import { QueryClient, DefaultOptions } from '@tanstack/react-query';
import { parseApiError, reportError } from './error-handling';

// Basic query defaults for different data types
const QUERY_DEFAULTS = {
  // Static data (rarely changes)
  STATIC: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  },
  // Dynamic data (changes occasionally)
  DYNAMIC: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
  },
  // Real-time data (changes frequently)
  REALTIME: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000, // 30 seconds fallback
  },
} as const;

// Default query options
const defaultQueryOptions: DefaultOptions = {
  queries: {
    retry: (failureCount, error: unknown) => {
      const appError = parseApiError(error);

      // Don't retry on client errors (4xx). AUTH/VALIDATION are 4xx and
      // already covered, plus any other 4xx classified as CLIENT.
      if (
        appError.category === 'AUTH' ||
        appError.category === 'VALIDATION' ||
        appError.category === 'CLIENT'
      ) {
        return false;
      }

      // Retry up to 3 times for network/server errors
      return failureCount < 3;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: QUERY_DEFAULTS.DYNAMIC.staleTime,
    gcTime: QUERY_DEFAULTS.DYNAMIC.gcTime,
    throwOnError: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  },
  mutations: {
    retry: (failureCount, error: unknown) => {
      const appError = parseApiError(error);

      // Only retry mutations on network errors
      if (appError.category === 'NETWORK') {
        return failureCount < 2;
      }

      return false;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
    throwOnError: false,
    onError: (error: unknown, variables: unknown) => {
      const appError = parseApiError(error);
      reportError(appError, {
        type: 'mutation',
        variables,
      });
    },
  },
};

// Create the QueryClient instance
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
});

// Basic cache invalidation helpers
export const cacheInvalidation = {
  // Clear all cached data (logout, tenant switch)
  clearAll: async (): Promise<void> => {
    queryClient.clear();
  },

  // Invalidate all data for a factory
  invalidateFactory: async (factoryId: string): Promise<void> => {
    await queryClient.invalidateQueries({
      predicate: query => {
        const { queryKey } = query;
        return Array.isArray(queryKey) && queryKey.includes(factoryId);
      },
    });
  },

  // Basic invalidation by query key pattern
  invalidateByPattern: async (keyPattern: readonly unknown[]): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: keyPattern });
  },
};

// Query configuration helpers
export const getQueryConfig = (
  type: keyof typeof QUERY_DEFAULTS
): (typeof QUERY_DEFAULTS)[keyof typeof QUERY_DEFAULTS] => {
  return QUERY_DEFAULTS[type];
};

// Export query client configuration for testing
export const getQueryClientConfig = (): DefaultOptions => defaultQueryOptions;

// Basic optimistic update helper
export const optimisticUpdates = {
  // Update data in cache optimistically
  updateData: <T>(
    queryKey: readonly unknown[],
    updater: (old: T | undefined) => T | undefined
  ): void => {
    queryClient.setQueryData(queryKey, updater);
  },

  // Remove data from cache
  removeData: (queryKey: readonly unknown[]): void => {
    queryClient.removeQueries({ queryKey });
  },
};
